import { useMemo, useState } from "react";
import { CalendarDays, Clock, BarChart3, Users } from "lucide-react";
import { Reveal, SectionHead, GoldBtn, PageHero } from "@/components/Shared";
import { MASTERCLASSES } from "@/data/content";

const TRACKS = ["All", ...new Set(MASTERCLASSES.map((m) => m.track))];

export default function KnowledgeLabs() {
    const [track, setTrack] = useState("All");
    const list = useMemo(() => (track === "All" ? MASTERCLASSES : MASTERCLASSES.filter((m) => m.track === track)), [track]);

    return (
        <div data-testid="knowledge-labs-page">
            <PageHero
                testid="labs-hero"
                eyebrow="Knowledge & Learning Labs"
                title={<>20+ Masterclasses. <span className="gold-text">Zero Fluff.</span></>}
                sub="Innovation studios led by top experts — fundraising, brand, AI, ESG, exports, legal and policy, taught by people who have actually built."
            >
                <GoldBtn to="/register?type=delegate" testid="labs-register-btn">Reserve a Seat</GoldBtn>
            </PageHero>

            <section className="py-16 md:py-24">
                <div className="container-x">
                    <Reveal>
                        <div className="flex flex-wrap gap-2" data-testid="labs-filters">
                            {TRACKS.map((t) => (
                                <button key={t} onClick={() => setTrack(t)} data-testid={`labs-filter-${t.toLowerCase()}`}
                                    className={`chip ${track === t ? "chip-on" : ""}`}>
                                    {t}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {list.map((m, i) => (
                            <Reveal key={m.title} delay={(i % 4) * 60}>
                                <div className="card-luxe tilt p-6 h-full flex flex-col" data-testid={`masterclass-card-${i}`}>
                                    <div className="card-glow" />
                                    <span className="text-[0.55rem] tracking-[0.3em] uppercase text-[var(--gold)]">{m.track}</span>
                                    <h3 className="font-display text-lg mt-3 leading-snug text-[var(--ivory)] flex-1">{m.title}</h3>
                                    <div className="mt-5 space-y-2 text-[0.62rem] tracking-[0.14em] uppercase text-muted-luxe">
                                        <p className="flex items-center gap-2"><CalendarDays size={11} className="text-[var(--gold)]" /> {m.date}</p>
                                        <p className="flex items-center gap-2"><Clock size={11} className="text-[var(--gold)]" /> {m.time}</p>
                                        <p className="flex items-center gap-2"><BarChart3 size={11} className="text-[var(--gold)]" /> {m.level}</p>
                                        <p className="flex items-center gap-2"><Users size={11} className="text-[var(--gold)]" /> {m.seats} Seats</p>
                                    </div>
                                    <GoldBtn to="/register?type=delegate" variant="outline" testid={`masterclass-register-${i}`} className="mt-6 w-full !py-2.5">
                                        Register
                                    </GoldBtn>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-14 text-center">
                        <p className="text-sm text-muted-luxe">
                            More masterclasses and innovation studios are in curation — the full 20+ session line-up will be announced soon.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60">
                <div className="container-x text-center">
                    <SectionHead eyebrow="Policy & Clinics" title={<>Startup Policy Clinics, <span className="gold-text">Live at the Labs</span></>}
                        sub="Navigate incentives, compliance and state startup missions with expert clinics running through all three days." />
                    <Reveal delay={150}>
                        <div className="mt-12">
                            <GoldBtn to="/contact" variant="outline" testid="labs-enquiry-btn">Ask About the Labs</GoldBtn>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
